// Package Identity — single source of truth for the branding of each
// exportable Claim Intelligence Package™ download variant.
//
// Every surface that must vary by package (cover, TOC intro, closing
// page, header/footer chrome, PDF metadata, and download filename) reads
// from this table. The PDF engine and the report route both consume it
// so the customer always sees a coherent identity for the product they
// purchased.

export type ReportPackage = "complete" | "review" | "negotiation";

export interface PackageIdentity {
  key: ReportPackage;
  /** Display title with ™ (used in TOC intro, closing copy, footers). */
  title: string;
  /** Bare display title without ™ (used in filenames, PDF metadata). */
  titlePlain: string;
  /** Cover title, one word per line (renders very large). */
  coverTitleLines: string[];
  /** Bold primary line inside the gold ribbon on the cover. */
  ribbonPrimary: string;
  /** Secondary line inside the gold ribbon on the cover. */
  ribbonSecondary: string;
  /** Body message on the closing "Thank you" page. */
  thankYouMessage: string;
  /** Primary line inside the closing gold ribbon (all caps). */
  closingRibbonPrimary: string;
  /** Secondary line inside the closing gold ribbon. */
  closingRibbonSecondary: string;
  /** Slug for the download filename (`ClaimGuard-<slug>-<id>.pdf`). */
  filenameSlug: string;

}

export const PACKAGE_IDENTITY: Record<ReportPackage, PackageIdentity> = {
  complete: {
    key: "complete",
    title: "Claim Intelligence Package™",
    titlePlain: "Claim Intelligence Package",
    coverTitleLines: ["CLAIM", "INTELLIGENCE", "PACKAGE"],
    ribbonPrimary: "Professional Invoice Review",
    ribbonSecondary: "Certified Professional Analysis",
    thankYouMessage:
      "Your Claim Intelligence Package has been successfully prepared. This complete report contains every finding, all supporting evidence, executive decision guidance, and the full negotiation strategy. We hope it helps you evaluate your invoice, understand the supporting evidence, and determine your next course of action.",
    closingRibbonPrimary: "Professional Review Complete",
    closingRibbonSecondary: "Certified Professional Analysis",
    filenameSlug: "Claim-Intelligence-Package",
  },
  review: {
    key: "review",
    title: "Professional Review Report™",
    titlePlain: "Professional Review Report",
    coverTitleLines: ["PROFESSIONAL", "REVIEW", "REPORT"],
    ribbonPrimary: "Professional Invoice Review",
    ribbonSecondary: "Market Analysis · Legal Review · Supporting Evidence",
    thankYouMessage:
      "Your Professional Review Report has been successfully prepared. This report documents the market analysis, legal review, and supporting evidence assembled by ClaimGuard Professional Review Services. We hope these findings help you evaluate your invoice and understand the supporting evidence.",
    closingRibbonPrimary: "Professional Review Complete",
    closingRibbonSecondary: "Certified Professional Analysis",
    filenameSlug: "Professional-Review",
  },
  negotiation: {
    key: "negotiation",
    title: "Negotiation Package™",
    titlePlain: "Negotiation Package",
    coverTitleLines: ["NEGOTIATION", "PACKAGE"],
    ribbonPrimary: "Executive Decision",
    ribbonSecondary: "Professional Negotiation Strategy · Settlement Playbook",
    thankYouMessage:
      "Your Negotiation Package has been successfully prepared. This package provides your executive negotiation summary, professional negotiation strategy, recommended settlement guidance, and structured conversation playbook to help you negotiate with confidence.",
    closingRibbonPrimary: "Negotiation Package Complete",
    closingRibbonSecondary: "Certified Professional Negotiation Strategy",
    filenameSlug: "Negotiation-Package",
  },
};


export function getPackageIdentity(pkg: ReportPackage | undefined | null): PackageIdentity {
  return PACKAGE_IDENTITY[pkg ?? "complete"] ?? PACKAGE_IDENTITY.complete;
}
